import { type FC, useEffect, useState } from "react";
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconAdd from "@mui/icons-material/Add";
import { type LazyQueryExecFunction, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import { useMutationWithExpiration } from "providers/Apollo/useMutationWithExpiration";
import { EmergencyContactFormList } from "../../EmergencyContactFormList/EmergencyContactFormList";
import { type ActivityQueryData, CONTACT_RELATIONSHIP_QUERY, UPDATE_EMERGENCY_CONTACTS_MUTATION } from "components/CaseManagement/ActivityDetails/queries";
import { type IActivityDetails } from "components/CaseManagement/interfaces";
import { Button } from "components/Shared/Inputs";
import { Spinner } from "components/Shared";

import "./EmergencyContact.scss";

export interface EmergencyContactTabProps {
    activity: IActivityDetails;
    refetchActivity: LazyQueryExecFunction<ActivityQueryData, { activityId: number; }>
}

const TextLabel = ({ value, label }) => {
    return <div>
        <p className='client_details_field_value'>{value}</p>
        <p className='client_details_field_label'>{label}</p>
    </div>
}

export const EmergencyContactTab: FC<EmergencyContactTabProps> = ({ activity, refetchActivity }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [inEdit, setInEdit] = useState(false);
    const { activityId, client: { clientEmergencyContacts: emergencyContacts } } = activity ?? {};
    const { i18n, t } = useTranslation("emergencyContact", { useSuspense: false });
    const noContacts = emergencyContacts?.length === 0;
    const { loading, data: relationshipData } = useQuery(CONTACT_RELATIONSHIP_QUERY, {
        fetchPolicy: "cache-first",
        variables: { language: i18n.language.substring(0,2) }
    });

    const relationships = relationshipData?.contactRelationships ?? [];
    
    const [updateEmergencyContacts, updateEmergencyContactsMutation] = useMutationWithExpiration(UPDATE_EMERGENCY_CONTACTS_MUTATION, {
        onCompleted: (data) => {
            if (data?.updateClientEmergencyContacts?.includes("Error")) {
                enqueueSnackbar(`${t('error_updating_contact_info')}... ${data.updateClientEmergencyContacts}`);
            } else {
                refetchActivity({ variables: { activityId }, fetchPolicy: 'network-only' });
            }          
        }
    });
    
    const TabContent = () => {
        const [uniqueKeys, setUniqueKeys] = useState<number[]>([Date.now()]);
        const labelSuffix = {
            0: `(${t('primary')})`,
            1: `(${t('secondary')})`,
            2: `(${t('tertiary')})`
        }
        
        useEffect(() => {
            for(let i = 1; i<emergencyContacts.length; i++) {
                setUniqueKeys([...uniqueKeys, Date.now() + i]);
            }
            return () => setUniqueKeys([]);
        }, []);

        if (noContacts && !inEdit) {
            return (
                <Grid container style={{ padding: '64px', justifyContent: "center" }} spacing={5}>
                    <Button size="large" variant="opaque" onClick={() => setInEdit(true)}><IconAdd />{t('add_emergency_contact')}</Button>
                </Grid>
            );
        }

        if (inEdit) {
            return (
                <EmergencyContactFormList
                    activity={activity}
                    onSubmit={updateEmergencyContacts}
                    busy={updateEmergencyContactsMutation.loading}
                    viewMode="tab"
                    onCancel={() => setInEdit(false)}
                />
            );
        } else {
            return (
                <Container maxWidth="md">
                    {
                        emergencyContacts?.map((emergencyContact, indx) => {
                            let relationshipText = relationships.find(rel => rel.contactRelationshipId === emergencyContact?.contactRelationshipId)?.description;
                            if (['other', 'autre'].includes(relationshipText?.toLowerCase() ?? '')) {
                                relationshipText = emergencyContact.otherRelationship!;
                            }
                            return (
                                <div key={uniqueKeys[indx]}>
                                    <h3>{t('emergency_contact')} {labelSuffix[indx]}</h3>
                                    <Grid container spacing={2}>
                                        <Grid item md={6}>
                                            <TextLabel value={emergencyContact?.firstName} label={t('first_name')} />
                                        </Grid>
                                        <Grid item md={6}>
                                            <TextLabel value={emergencyContact?.lastName} label={t('last_name')} />
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid item md={6}>
                                            <TextLabel value={emergencyContact?.phoneNumber} label={t('phone_number')} />
                                        </Grid>
                                        <Grid item md={6}>
                                            {loading ? <Spinner />
                                                :
                                                <TextLabel
                                                    value={relationshipText ?? t('n_a', 'N/A')}
                                                    label={t('relationship')}
                                                />
                                            }
                                        </Grid>
                                    </Grid>
                                    {emergencyContacts.length - 1 > indx && <Divider />}
                                </div>
                            );
                        })
                    }
                </Container>
            );
        }
    }

    return (
            <div className="activity_details_content_wrapper">
                <Container maxWidth="md">
                    <Card className="emergency-contact-page-card" elevation={2}>
                        <Grid container>
                            <Grid item xs={12} md={10}>
                                <h2>{t('emergency_contact_info')}</h2>
                                {!noContacts && <div>{t('to_edit_add_or_delete_click_modify')}</div>}
                            </Grid>
                            {!noContacts && (
                                <Grid item xs={12} md={2} style={{ justifyContent: 'center' }}>
                                    <Button variant="opaque" onClick={() => setInEdit(!inEdit)} disabled={inEdit}>{t('modify')}</Button>
                                </Grid>
                            )}
                        </Grid>
                        <TabContent />
                    </Card>
                </Container>
            </div>
    );
}
